import { useQuery } from '@tanstack/react-query';

export interface GeoPoint {
  pincode: number;
  found: boolean;
  lat: number | null;
  lng: number | null;
  city: string | null;
  state: string | null;
}

// The geo endpoint is public and lives on the same gateway the admin + public apps already use.
const GEO_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'https://backend.shiproute.demo/v1';

/**
 * Resolve a set of pincodes to lat/lng (cached server-side). Accepts any mix of values; only valid
 * 6-digit pincodes are queried. Returns the react-query state plus a `get(pincode)` lookup helper
 * that yields only successfully-geocoded points.
 */
export function useGeocode(pincodes: Array<string | number | null | undefined>) {
  const codes = Array.from(
    new Set(pincodes.map((p) => String(p ?? '').trim()).filter((p) => /^\d{6}$/.test(p))),
  );
  const key = codes.slice().sort().join(',');

  const query = useQuery<GeoPoint[]>({
    queryKey: ['geo-pincodes', key],
    queryFn: async () => {
      if (!codes.length) return [];
      const res = await fetch(`${GEO_BASE}/geo/pincodes?codes=${encodeURIComponent(codes.join(','))}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: codes.length > 0,
    staleTime: 24 * 60 * 60 * 1000,
    retry: 1,
  });

  const byPincode = new Map<number, GeoPoint>();
  for (const g of query.data ?? []) if (g?.found && g.lat != null && g.lng != null) byPincode.set(g.pincode, g);

  const get = (pin: string | number | null | undefined): GeoPoint | undefined => {
    const n = Number(String(pin ?? '').trim());
    return Number.isInteger(n) ? byPincode.get(n) : undefined;
  };

  return { ...query, get, byPincode };
}
